/* eslint-disable no-redeclare, no-case-declarations, no-inner-declarations */
import store from '../'
import axios from 'axios'
import {router} from '../../router/router.js'
import {SERVER_PATH} from '../../config'
import {functions} from '../../functions'
import { EventBus } from '../../eventBus'
import { createBuildSlots, createMenuOptions, getPartDefinition, orderedMenuOptions } from '../../partOrder'
import { isPartAvailable } from '../../utils/partAvailability'
const BUILD_STORAGE_KEY = 'gixcore-current-build'

const createBuildRaw = () => ({
    processor_id:   '',
    motherboard_id: '',
    ram_id:         [],
    storage_id:     [],
    graphics_id:    [],
    psu_id:         '',
    case_id:        '',
})

const canUseLocalStorage = () => typeof window !== 'undefined' && window.localStorage

const readPersistedBuildState = () => {
    if(!canUseLocalStorage()){
        return null
    }
    try {
        var rawState = window.localStorage.getItem(BUILD_STORAGE_KEY)
        if(!rawState){
            return null
        }
        return JSON.parse(rawState)
    }
    catch(e){
        return null
    }
}

const numericOr = (value, fallback) => {
    var numericValue = Number(value)
    return isNaN(numericValue) ? fallback : numericValue
}

const buildRawKeyForType = (type) => {
    var normalizedType = type
    if(normalizedType == 'cpu'){
        normalizedType = 'processor'
    }
    if(normalizedType == 'gpu'){
        normalizedType = 'graphics'
    }
    return normalizedType + '_id'
}

// A stable fingerprint of which parts are in the build. Compared against the
// fingerprint captured when a saved build was opened (or last written) to tell
// whether there are unsaved changes.
//
// A boolean "dirty" flag was the obvious alternative and is wrong: it trips on
// load-order noise, cannot survive a page reload, and stays true after a part is
// swapped and swapped back. Multi-slot ids are sorted because two RAM sticks in
// the other order is not an edit.
const buildSignature = (buildRaw) => {
    if(!buildRaw){ return '' }
    return Object.keys(buildRaw).sort().map((key) => {
        var value = buildRaw[key]
        var ids = []
        if(Array.isArray(value)){
            ids = value
        }
        else if(typeof value == 'string' && value.charAt(0) == '['){
            try { ids = JSON.parse(value) } catch(e) { ids = [value] }
        }
        else if(value !== '' && value !== null && value !== undefined){
            ids = [value]
        }
        return key + ':' + ids.map(String).sort().join(',')
    }).join('|')
}

// Saving used to surface a JSON-stringified axios error object. Map the statuses
// that actually happen onto something a builder can act on.
//
// 422 is the common one and is not a bug in the request: the API refuses build
// rows that reference parts no longer offered in the catalog, which is exactly
// what an older saved build (or a long-lived local build) ends up holding once a
// part is archived.
const saveErrorMessage = (error) => {
    var response = error && error.response ? error.response : null
    var status = response ? response.status : 0

    if(status === 422){
        // The API names no field, so neither do we -- promising a highlight the
        // checklist does not render is worse than saying plainly what happened.
        return 'The catalog no longer offers one or more of the selected parts, so the server refused this build. Swapping those parts for current options lets it save.'
    }
    if(status === 401 || status === 403){
        return 'Your session has expired. Sign in again to save this build.'
    }

    var data = response ? response.data : null
    if(data && typeof data === 'object' && data.sqlMessage){
        return String(data.sqlMessage)
    }
    if(typeof data === 'string' && data.trim() && data.trim().length < 200){
        return data.trim()
    }
    return 'Could not save this build. Please try again.'
}

const persistBuildState = (state) => {
    if(!canUseLocalStorage()){
        return
    }
    try {
        window.localStorage.setItem(BUILD_STORAGE_KEY, JSON.stringify({
            menuOptions: state.menuOptions,
            build: state.build,
            buildRaw: state.buildRaw,
            buildTDP: state.buildTDP,
            totalCost: state.totalCost,
            // Persisted so "editing build #42, with changes" survives a refresh.
            savedBuildId: state.savedBuildId,
            savedBuildName: state.savedBuildName,
            savedSignature: state.savedSignature,
            draftBuildName: state.draftBuildName,
            // Automatic titles may continue evolving as CPU/GPU choices change.
            // Once the user types, this flips false and their wording is sacred.
            draftBuildNameAutomatic: state.draftBuildNameAutomatic,
        }))
    }
    catch(e){
        // Persisting the in-progress build should never block the builder UI.
    }
}

const cloneBuildValue = (value) => {
    if(value === undefined){ return null }
    return JSON.parse(JSON.stringify(value))
}

const buildSlotSnapshot = (state, type) => {
    var definition = getPartDefinition(type)
    if(!definition){ return null }
    var buildSlot = state.build.find((slot) => slot.type == definition.buildType)
    var menuOption = state.menuOptions[definition.menuKey]
    var rawKey = definition.backendKey + '_id'
    return {
        type: definition.key,
        buildContents: cloneBuildValue(buildSlot ? buildSlot.contents : null),
        menuContents: cloneBuildValue(menuOption && menuOption.item ? menuOption.item.contents : null),
        rawValue: cloneBuildValue(state.buildRaw[rawKey]),
    }
}

const buildStateSnapshot = (state) => ({
    menuOptions: cloneBuildValue(state.menuOptions),
    build: cloneBuildValue(state.build),
    buildRaw: cloneBuildValue(state.buildRaw),
})

const writeBuildSlot = (state, snapshot) => {
    var definition = getPartDefinition(snapshot && snapshot.type)
    if(!definition){ return }
    var buildSlot = state.build.find((slot) => slot.type == definition.buildType)
    var menuOption = state.menuOptions[definition.menuKey]
    if(buildSlot){
        buildSlot.contents = cloneBuildValue(snapshot.buildContents)
    }
    if(menuOption && menuOption.item){
        menuOption.item.contents = cloneBuildValue(snapshot.menuContents)
    }
    state.buildRaw[definition.backendKey + '_id'] = cloneBuildValue(snapshot.rawValue)
    persistBuildState(state)
}

const persistedBuildState = readPersistedBuildState()
const state = {
    menuOptions: persistedBuildState && persistedBuildState.menuOptions ? persistedBuildState.menuOptions : createMenuOptions(),
    build: persistedBuildState && persistedBuildState.build ? persistedBuildState.build : createBuildSlots(),
    compatLib: {},
    compatTable: {},
    stateCompatTable: {},
    buildRaw : persistedBuildState && persistedBuildState.buildRaw ? persistedBuildState.buildRaw : createBuildRaw(),
    buildTDP: persistedBuildState ? numericOr(persistedBuildState.buildTDP, 0) : 0,
    totalCost: persistedBuildState ? numericOr(persistedBuildState.totalCost, 0) : 0,
    processing: false,
    recentBuilds: [],
    buildsLoaded: false,
    compatibleMode: true,
    buildStatus:    'ok',
    warningsTable: {},
    warnings: [],
    // Identity of the saved build currently open in the builder. Null means this
    // is a new build that has never been written to the account.
    savedBuildId:   persistedBuildState ? (persistedBuildState.savedBuildId || null) : null,
    savedBuildName: persistedBuildState ? (persistedBuildState.savedBuildName || '') : '',
    savedSignature: persistedBuildState ? (persistedBuildState.savedSignature || '') : '',
    // The working title, editable in the checklist. Separate from savedBuildName
    // so renaming counts as an unsaved change like any part swap does.
    draftBuildName: persistedBuildState ? (persistedBuildState.draftBuildName || '') : '',
    draftBuildNameAutomatic: persistedBuildState && typeof persistedBuildState.draftBuildNameAutomatic == 'boolean'
        ? persistedBuildState.draftBuildNameAutomatic
        : !(persistedBuildState && persistedBuildState.draftBuildName),
};

const buildHasParts = (buildRaw) => {
    if(!buildRaw){ return false }
    return Object.keys(buildRaw).some((key) => {
        var value = buildRaw[key]
        if(Array.isArray(value)){ return value.length > 0 }
        if(typeof value == 'string' && value.charAt(0) == '['){
            try { return JSON.parse(value).length > 0 } catch(e) { return false }
        }
        return value !== '' && value !== null && value !== undefined
    })
}
const getters = {
    getWarnings:                (state) => state.warnings,
    getBuidStatus:              (state) => state.buildStatus,
    getCompatibleMode:          (state) => state.compatibleMode,
    getCompatLib:               (state) => state.compatLib,
    getBuildTDP:                (state) => state.buildTDP,
    getStateCompatTable:        (state) => state.stateCompatTable,
    getCompatTable:             (state) => state.compatTable,
    ifProcessing:               (state) => state.processing,
    allMenuOptions:             (state) => orderedMenuOptions(state.menuOptions),
    getBuild:                   (state) => state.build,
    getBuildRaw:                (state) => state.buildRaw,
    getTotalCost:               (state) => state.totalCost,
    getRecentBuilds:            (state) => state.recentBuilds,
    getBuildsLoaded:            (state) => state.buildsLoaded,
    getSavedBuildId:            (state) => state.savedBuildId,
    getSavedBuildName:          (state) => state.savedBuildName,
    isEditingSavedBuild:        (state) => state.savedBuildId !== null
                                        && state.savedBuildId !== undefined
                                        && state.savedBuildId !== '',
    getDraftBuildName:          (state) => state.draftBuildName,
    getDraftBuildNameAutomatic: (state) => state.draftBuildNameAutomatic,
    buildHasParts:              (state) => buildHasParts(state.buildRaw),
    // Saving is one click, so an untitled build still needs a name. Lead with the
    // part people recognise the build by rather than a bare "Untitled build".
    suggestedBuildName:         (state) => {
        var options = state.menuOptions || {}
        var pick = (key) => {
            var contents = options[key] && options[key].item ? options[key].item.contents : null
            var item = Array.isArray(contents) ? contents[0] : contents
            return item && item.model ? String(item.model).trim() : ''
        }
        var lead = pick('cpu') || pick('graphics') || pick('motherboard')
        return lead ? lead + ' build' : 'Untitled build'
    },
    // Only meaningful while a saved build is open: a brand-new build is already
    // persisted to localStorage, so there is nothing for the user to lose.
    // A rename counts as a change -- otherwise the title could be edited and the
    // Save button would stay disabled.
    hasUnsavedChanges:          (state) => {
        if(state.savedBuildId === null || state.savedBuildId === undefined || state.savedBuildId === ''){
            return false
        }
        if(state.draftBuildName.trim() !== state.savedBuildName.trim()){
            return true
        }
        return buildSignature(state.buildRaw) !== state.savedSignature
    },
    // Drives the Save button. An untouched saved build has nothing to write, and
    // an empty build has nothing to save at all.
    canSaveBuild:               (state, getters) => {
        if(!buildHasParts(state.buildRaw)){ return false }
        if(!getters.isEditingSavedBuild){ return true }
        return getters.hasUnsavedChanges
    },
};

const compatValue = (item, field) => {
    if(!item || !item.compat || item.compat[field] === undefined || item.compat[field] === null){
        return ''
    }
    return String(item.compat[field]).toLowerCase()
}

const compatCount = (item, field) => {
    var parsed = parseInt(item && item.compat ? item.compat[field] : 0)
    return isNaN(parsed) ? 0 : parsed
}

const slotContents = (slot) => {
    var contents = slot && slot.item ? slot.item.contents : null
    if(!contents){ return [] }
    if(Array.isArray(contents)){ return contents }
    return [contents]
}

const isM2Storage = (item) => {
    if(!item || item.type !== 'storage'){ return false }
    var size = compatValue(item, 'size')
    var interfaceValue = compatValue(item, 'compat_interface')
    return size.includes('m.2')
        || size.includes('m2')
        || interfaceValue.includes('m.2')
        || interfaceValue.includes('m2')
        || interfaceValue.includes('nvme')
        || interfaceValue.includes('pci')
}

const isSataStorage = (item) => {
    return !!(item && item.type == 'storage' && !isM2Storage(item))
}

const countStorageBySlot = (items, slotType) => {
    var total = 0
    if(!items || !items.length){ return total }
    items.forEach((existingItem) => {
        if(slotType == 'm2' && isM2Storage(existingItem)){ total++ }
        if(slotType == 'sata' && isSataStorage(existingItem)){ total++ }
    })
    return total
}

// Which drives the board's M.2 slots take: compats.m_2 ("PCIe", "SATA",
// "SATA/PCIe") when it names a bus, else the older Yes/No compats.nvme, whose
// "Yes" accepts both kinds of M.2 drive. Mirror of backend CompatComparators.m2Mode.
const m2Mode = (motherboard) => {
    var m2 = compatValue(motherboard, 'm_2')
    if(/pci|nvme|sata/.test(m2)){ return m2 }
    return compatValue(motherboard, 'nvme')
}

const motherboardSupportsM2Mode = (storage, motherboard) => {
    var mode = m2Mode(motherboard)
    var interfaceValue = compatValue(storage, 'compat_interface')
    if(!mode || mode == 'no' || mode == 'n/a'){ return false }
    if(interfaceValue.includes('sata') && !(mode.includes('sata') || mode.includes('yes'))){ return false }
    if((interfaceValue.includes('pci') || interfaceValue.includes('nvme')) && !(mode.includes('pci') || mode.includes('nvme') || mode.includes('yes'))){ return false }
    return true
}

const storageInterfaceCompatibleWithMotherboard = (storage, motherboard) => {
    if(!storage || !motherboard){ return false }
    if(isSataStorage(storage)){
        return compatCount(motherboard, 'sata_slots') > 0
    }
    if(isM2Storage(storage)){
        return compatCount(motherboard, 'M_2_Slot') > 0 && motherboardSupportsM2Mode(storage, motherboard)
    }
    return false
}

const processorGenerationSupported = (cpuGeneration, motherboardGenerations) => {
    var cpuGen = parseInt(cpuGeneration)
    if(isNaN(cpuGen) || !motherboardGenerations){ return false }
    var generationTokens = String(motherboardGenerations)
        .split(/[-,_ ]+/)
        .map((generation) => parseInt(generation))
        .filter((generation) => !isNaN(generation))
    if(generationTokens.length === 0){ return false }
    if(generationTokens.includes(cpuGen)){ return true }
    return false
}

// Memory generations named by a spec string, as a de-duped set.
// LPDDR5 is soldered mobile memory, NOT DDR5, so it is captured as its own token —
// the alternation must stay greedy-first or the bare DDR5 pattern matches inside
// LPDDR5 and equates the two. No lookbehind: this ships to older browsers.
const memoryGenerations = (value) => {
    var found = String(value == null ? '' : value).toUpperCase().match(/(?:LP)?DDR[2-6]/g) || []
    var out = []
    found.forEach((token) => { if(out.indexOf(token) === -1){ out.push(token) } })
    return out
}

// Memory-type fit (source of truth for DDR generations). Replaces the old substring
// includes(), which only worked while one side listed a single generation: an
// LGA1700 CPU accepting "DDR4, DDR5" and a board listing "DDR5, DDR4" contain each
// other nowhere, so a valid pair was rejected. Ordering and separators are scrape
// artifacts — the parts fit if the two sides share ANY generation.
// Values naming no generation at all fall back to the old substring behaviour.
const memoryTypesCompatible = (a, b) => {
    var x = memoryGenerations(a)
    var y = memoryGenerations(b)
    if(!x.length || !y.length){
        var sa = String(a == null ? '' : a).toLowerCase()
        var sb = String(b == null ? '' : b).toLowerCase()
        if(!sa || !sb){ return false }
        return sa.includes(sb) || sb.includes(sa)
    }
    return x.some((generation) => y.indexOf(generation) !== -1)
}

// PSU form-factor fit (source of truth for PSU sizing). Replaces the old substring
// includes() that mis-modeled PSU sizes (e.g. rejecting an SFX-L PSU for an ATX build).
// Keyed by normalized PSU form factor; values are the build/case sizes (normalized,
// no spaces/hyphens) the PSU physically fits. Easy to extend later (TFX, Flex-ATX...).
const PSU_FORM_FACTOR_FIT = {
    // Small PSUs fit any standard case (adapter bracket for the larger ones).
    'sfx':   ['miniitx', 'microatx', 'atx'],
    'sfx-l': ['miniitx', 'microatx', 'atx'],
    // Full-size ATX PSU is too large for mini-ITX / small-form-factor builds.
    'atx':   ['microatx', 'atx'],
}

// Build/case sizes this map can decide on. Anything outside it (e.g. E-ATX, nano-ITX)
// is left undecided so we don't hard-block — a smaller-or-equal PSU generally still fits.
const KNOWN_BUILD_SIZES = ['miniitx', 'microatx', 'atx']

const normalizePsuFormFactor = (formFactor) => {
    var normalized = String(formFactor || '').toLowerCase().replace(/\s+/g, '')
    if(normalized === 'sfxl'){ normalized = 'sfx-l' }   // tolerate "SFX L" / "SFXL"
    return normalized
}

const normalizeBuildFormFactor = (formFactor) => {
    return String(formFactor || '').toLowerCase().replace(/[\s-]+/g, '')
}

// true = fits, false = does not fit, null = unknown (fall back, don't hard-block).
const psuFormFactorFits = (psuFormFactor, buildFormFactor) => {
    var allowedSizes = PSU_FORM_FACTOR_FIT[normalizePsuFormFactor(psuFormFactor)]
    if(!allowedSizes){ return null }                                // unknown PSU form factor
    var buildSize = normalizeBuildFormFactor(buildFormFactor)
    if(!KNOWN_BUILD_SIZES.includes(buildSize)){ return null }       // unknown build/case size
    return allowedSizes.includes(buildSize)
}

// Case <-> motherboard form-factor ladder (source of truth; the backend mirrors
// this). A case can list MULTIPLE accepted board sizes, comma/;/|//-separated,
// e.g. "mATX, Mini-ITX". A board fits if it is <= any listed case size; we take
// the best result across the listed sizes (compatible > warning > blocked) and
// only block when the board is larger than EVERY listed size (case too small).
const FORM_FACTOR_RANK = {
    nanoitx:  0,
    miniitx:  1,
    microatx: 2, matx: 2,   // "mATX" normalizes to "matx"
    miniatx:  3,
    atx:      4,
}

// Normalize a single form factor token: lowercase, strip spaces + dashes (as before).
const normFormFactor = (value) => String(value || '')
    .toLowerCase()
    .split('')
    .filter((ch) => ch !== ' ' && ch !== '-')
    .join('')

// Split a (possibly multi-value) case form factor into tokens.
const caseFormFactorTokens = (value) => String(value || '')
    .split(/[,;/|]+/)
    .map((token) => token.trim())
    .filter((token) => token !== '')

// Best-of fit of a single board form factor against a (possibly multi-value) case.
// Returns { kind: 'compatible'|'warning'|'blocked', reason: 'fit'|'bigger'|'small'|'mismatch' }.
const caseBoardFormFactorFit = (boardFormFactor, caseFormFactor) => {
    const boardNorm = normFormFactor(boardFormFactor)
    const boardRank = FORM_FACTOR_RANK[boardNorm]
    const tokens = caseFormFactorTokens(caseFormFactor)
    if (!tokens.length) {
        return { kind: 'blocked', reason: 'mismatch' }
    }
    const kindRank = { compatible: 2, warning: 1, blocked: 0 }
    let best = { kind: 'blocked', reason: 'mismatch' }
    tokens.forEach((token) => {
        const caseNorm = normFormFactor(token)
        let result
        if (boardNorm && caseNorm && boardNorm === caseNorm) {
            result = { kind: 'compatible', reason: 'fit' }
        }
        else {
            const caseRank = FORM_FACTOR_RANK[caseNorm]
            if (boardRank === undefined || caseRank === undefined) {
                result = { kind: 'blocked', reason: 'mismatch' }   // unknown size -> behave as before
            }
            else if (boardRank === caseRank) {
                result = { kind: 'compatible', reason: 'fit' }
            }
            else if (boardRank < caseRank) {
                result = { kind: 'warning', reason: 'bigger' }     // case bigger than board
            }
            else {
                result = { kind: 'blocked', reason: 'small' }      // case smaller than board
            }
        }
        if (kindRank[result.kind] > kindRank[best.kind]) {
            best = result
        }
        else if (result.kind === 'blocked' && best.kind === 'blocked' && result.reason === 'small') {
            best = result   // among blocks, prefer the informative "too small" reason
        }
    })
    return best
}

const actions = {
    compatMode({commit}, bool){
        commit('SET_COMPAT_MODE', bool)
    },
    updateBuildStatus({commit}, status){
        commit('UPDATE_BUILD_STATUS', status)
    },
    async fetchRecentBuilds ({commit, state}, {from, limit}){
        commit('BUILDS_LOADED', false)
        if(!from && limit && limit >0){
            await axios({
                method: 'GET',
                url: `${SERVER_PATH}builds/builds?limit=${limit}`,
            }).then((response) => {
                commit('RECORD_FETCHED_BUILDS', response.data.Builds)
                commit('BUILDS_LOADED', true)
                console.log('fetched recent builds ', state.recentBuilds, ' builds Loaded: ', state.buildsLoaded)
            }).catch((err) => {
                commit('BUILDS_LOADED', false)
                console.log(err)
            })
        }
    },
    async isCompatible      ({commit, state }, {item, withType, option, forState, forLib}){
        // If Item exists in build
        var recordCompat = 'RECORD_COMPAT'
        if(forState == true){
            recordCompat = 'RECORD_STATE_COMPAT'
        }
        if(forLib == true){
            recordCompat = "RECORD_LIB_COMPAT"
        }
        var buildItem
        if(withType == 'general'){
            buildItem = {
                item: {
                    contents:{
                        power: state.buildTDP
                    }
                }
            }
        }
        else {
            buildItem = state.menuOptions[withType]
        }
        if(buildItem && buildItem.item.contents !== null && item){
            // Checking compatibility with received item
            if(!item.compat && item.item && item.item.contents) {
                var tempItem = item.item.contents
                item = tempItem
            }
            if(item.compat){
                // Which type is being viewed by user               
                switch (item.type){                                 // <<<<<<<<<< THIS
                    // Processor        Compatibility Check
                    case 'cpu':
                        // Item which is selected in user's build 
                        switch (withType){                          // <<<<<<<<<< WITH THIS ITEM IN BUILD
                            case 'motherboard':
                                switch (option){
                                    case 'socket':
                                        if (buildItem){
                                            let buildItemSocket = buildItem.item.contents.compat.socket
                                            let itemSocket = item.compat.socket
                                            if(buildItemSocket.includes(itemSocket) || itemSocket.includes(buildItemSocket)){
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: true, message: ''})
                                            }
                                            else {
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: false, message: 'motherboard socket ' + buildItem.item.contents.compat.socket + ' is incompatible with socket' + item.compat.socket + 'on the cpu'})
                                            }
                                        }
                                    break
                                    case 'ram_speed':
                                        if (buildItem){
                                            if(parseInt(buildItem.item.contents.compat.ram_speed) == parseInt(item.compat.ram_speed)){
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: true, message: ''})
                                            }
                                            else if(parseInt(buildItem.item.contents.compat.ram_speed) < parseInt(item.compat.ram_speed) || parseInt(buildItem.item.contents.compat.ram_speed) > parseInt(item.compat.ram_speed)){
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: true, warning: true, message: `RAM SPEED MISMATCH \n, your computer will run at ${parseInt(item.compat.ram_speed) < parseInt(buildItem.item.contents.compat.ram_speed) ? parseInt(item.compat.ram_speed) : parseInt(buildItem.item.contents.compat.ram_speed)} Mhz`})
                                            }
                                            // else {
                                            //     commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: false, message: 'cpu ram speed ' + buildItem.item.contents.compat.ram_speed + ' is incompatible with' + item.compat.ram_speed + 'on the motherboard'})
                                            // }
                                        }
                                        break
                                    case 'ram_type':
                                        if (buildItem){
                                            if(memoryTypesCompatible(buildItem.item.contents.compat.ram, item.compat.ram)){
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: true, message: ''})
                                            }
                                            else {
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: false, message: 'cpu ram ' + buildItem.item.contents.compat.ram + ' is incompatible with' + item.compat.ram + 'on the motherboard'})
                                            }
                                        }
                                        break
                                    case 'processor_generation':
                                        if (buildItem){
                                            if(compatValue(buildItem.item.contents, 'cpu_brand') == compatValue(item, 'brand')){
                                                var value = processorGenerationSupported(item.compat.processor_generation, buildItem.item.contents.compat.processor_generation)
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: value, message: ''})
                                            }
                                            else {
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: false, message: 'cpu ram speed ' + buildItem.item.contents.compat.ram_speed + ' is incompatible with' + item.compat.ram_speed + 'on the motherboard'})
                                            }
                                        }
                                        break
                                    }
                                break
                            case 'ram':
                                switch (option){
                                    case 'ram_type':
                                        if (buildItem && buildItem.item.contents){
                                            if(memoryTypesCompatible(item.compat.ram, buildItem.item.contents[0].compat.ram_type)){
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: true, message: ''})
                                            }
                                            else {
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: false, message: 'cpu ram ' + buildItem.item.contents[0].compat.ram_type + ' is incompatible with' + item.compat.ram + 'on the motherboard'})
                                            }
                                        }
                                        break
                                    case 'ram_speed':
                                        if (buildItem){
                                            if(parseInt(buildItem.item.contents[0].compat.ram_speed) == parseInt(item.compat.ram_speed)){
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: true, message: ''})
                                            }
                                            else if(parseInt(buildItem.item.contents[0].compat.ram_speed) < parseInt(item.compat.ram_speed) || parseInt(buildItem.item.contents[0].compat.ram_speed) > parseInt(item.compat.ram_speed)){
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: true, warning: true, message: `RAM SPEED MISMATCH \n your computer will run at ${parseInt(item.compat.ram_speed) < parseInt(buildItem.item.contents[0].compat.ram_speed) ? parseInt(item.compat.ram_speed) : parseInt(buildItem.item.contents[0].compat.ram_speed)} Mhz`})
                                            }
                                            // else {
                                            //     commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: false, message: 'cpu ram ' + buildItem.item.contents[0].compat.ram_type + ' is incompatible with' + item.compat.ram + 'on the motherboard'})
                                            // }
                                        }
                                        break
                                    case 'max_memory':
                                        if (buildItem){
                                            var totalRam = 0
                                            if(buildItem.item.contents && buildItem.item.contents.length >0){
                                                for(var j = 0; j < buildItem.item.contents.length; j++){
                                                    totalRam += parseInt(buildItem.item.contents[j].compat.capacity)
                                                }
                                                if(totalRam <= parseInt(item.specs.Max_Memory_Size)){
                                                    commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: true, message: ''})
                                                }
                                            }
                                            else {
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: false, message: 'total ram  ' + totalRam + 'GB is more than ' + item.specs.Max_Memory_Size + ' maximum ram size on the motherboard'})
                                            }
                                        }
                                        break
                                }
                                break
                        }    
                        break
                    // Motherboard      Compatibility Check
                    case 'motherboard':
                        switch (withType){                          // <<<<<<<<<< WITH THIS
                            case 'cpu':
                                switch (option){
                                    case 'socket':
                                        if (buildItem){
                                            if(buildItem.item.contents.compat.socket.includes(item.compat.socket) || item.compat.socket.includes(buildItem.item.contents.compat.socket)){
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: true, message: ''})
                                            }
                                            else {
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: false, message: 'cpu socket ' + buildItem.item.contents.compat.socket + ' is incompatible with socket' + item.compat.socket + 'on the motherboard'})
                                            }
                                        }
                                        break
                                    case 'ram_type':
                                        if (buildItem){
                                            if(memoryTypesCompatible(buildItem.item.contents.compat.ram, item.compat.ram)){
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: true, message: ''})
                                            }
                                            else {
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: false, message: 'cpu ram ' + buildItem.item.contents.compat.ram + ' is incompatible with' + item.compat.ram + 'on the motherboard'})
                                            }
                                        }
                                        break
                                    case 'ram_speed':
                                        if (buildItem){
                                            if(parseInt(buildItem.item.contents.compat.ram_speed) == parseInt(item.compat.ram_speed)){
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: true, message: ''})
                                            }
                                            else if(parseInt(buildItem.item.contents.compat.ram_speed) < parseInt(item.compat.ram_speed) || parseInt(buildItem.item.contents.compat.ram_speed) > parseInt(item.compat.ram_speed)){
                                                if(state.menuOptions.ram.item.contents){
                                                    if((parseInt(state.menuOptions.ram.item.contents[0].compat.ram_speed) < parseInt(buildItem.item.contents.compat.ram_speed))
                                                    && (parseInt(state.menuOptions.ram.item.contents[0].compat.ram_speed) < parseInt(item.compat.ram_speed))
                                                    ){
                                                        commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: true, warning: true, message: `RAM SPEED MISMATCH \n your computer will run at ${parseInt(state.menuOptions.ram.item.contents[0].compat.ram_speed)} Mhz`})
                                                    }
                                                }
                                                else{
                                                    commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: true, warning: true, message: `RAM SPEED MISMATCH \n your computer will run at ${parseInt(item.compat.ram_speed) < parseInt(buildItem.item.contents.compat.ram_speed) ? parseInt(item.compat.ram_speed) : parseInt(buildItem.item.contents.compat.ram_speed)} Mhz`})
                                                }
                                            }
                                            // else {
                                            //     commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: false, message: 'cpu ram speed ' + buildItem.item.contents.compat.ram_speed + ' is incompatible with' + item.compat.ram_speed + 'on the motherboard'})
                                            // }
                                        }
                                        break
                                    case 'processor_generation':
                                        if (buildItem){
                                            if(compatValue(buildItem.item.contents, 'brand') == compatValue(item, 'cpu_brand')){
                                                var value = processorGenerationSupported(buildItem.item.contents.compat.processor_generation, item.compat.processor_generation)
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: value, message: ''})
                                            }
                                            else {
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: false, message: 'cpu ram speed ' + buildItem.item.contents.compat.ram_speed + ' is incompatible with' + item.compat.ram_speed + 'on the motherboard'})
                                            }
                                        }
                                        break
                                }
                                break
                            case 'ram':
                                switch (option){
                                    case 'ram_type':
                                        if (buildItem){
                                            if(memoryTypesCompatible(buildItem.item.contents[0].compat.ram_type, item.compat.ram)){
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: true, message: ''})
                                            }
                                            else {
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: false, message: 'cpu ram ' + buildItem.item.contents[0].compat.ram_type + ' is incompatible with' + item.compat.ram + 'on the motherboard'})
                                            }
                                        }
                                        break
                                    case 'ram_speed':
                                        if (buildItem){
                                            if(parseInt(buildItem.item.contents[0].compat.ram_speed) == parseInt(item.compat.ram_speed)){
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: true, message: ''})
                                            }
                                            else if(parseInt(buildItem.item.contents[0].compat.ram_speed) < parseInt(item.compat.ram_speed) || parseInt(buildItem.item.contents[0].compat.ram_speed) > parseInt(item.compat.ram_speed)){
                                                if(state.menuOptions.cpu.item.contents){
                                                    if((parseInt(state.menuOptions.cpu.item.contents.compat.ram_speed) < parseInt(buildItem.item.contents[0] ? buildItem.item.contents[0].compat.ram_speed : buildItem.item.contents.compat.ram_speed))
                                                    && (parseInt(state.menuOptions.cpu.item.contents.compat.ram_speed) < parseInt(item.compat ? item.compat.ram_speed : item[0].compat.ram_speed))
                                                    ){
                                                        commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: true, warning: true, message: `RAM SPEED MISMATCH \n your computer will run at ${parseInt(state.menuOptions.cpu.item.contents.compat.ram_speed)} Mhz`})
                                                    }
                                                }
                                                else{
                                                    commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: true, warning: true, message: `RAM SPEED MISMATCH \n your computer will run at ${parseInt(item.compat.ram_speed) < parseInt(buildItem.item.contents[0].compat.ram_speed) ? parseInt(item.compat.ram_speed) : parseInt(buildItem.item.contents[0].compat.ram_speed)} Mhz`})
                                                }
                                            }
                                            // else {
                                            //     commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: false, message: 'cpu ram ' + buildItem.item.contents[0].compat.ram_type + ' is incompatible with' + item.compat.ram + 'on the motherboard'})
                                            // }
                                        }
                                        break
                                    case 'number_of_items':
                                        if (buildItem){
                                            var totalSticks = 0
                                            if(buildItem.item.contents && buildItem.item.contents.length >0){
                                                for(var j = 0; j < buildItem.item.contents.length; j++){
                                                    totalSticks += parseInt(buildItem.item.contents[j].compat.number_of_items)
                                                }
                                            }
                                            if(totalSticks <= parseInt(item.compat.ram_slots)){
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: true, message: ''})
                                            }
                                            else {
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: false, message: 'number of ram sticks ' + totalSticks + ' is more than ' + item.compat.ram_slots + ' slots on the motherboard'})
                                            }
                                        }
                                        break
                                    case 'max_memory':
                                        if (buildItem){
                                            var totalRam = 0
                                            if(buildItem.item.contents && buildItem.item.contents.length >0){
                                                for(var j = 0; j < buildItem.item.contents.length; j++){
                                                    totalRam += parseInt(buildItem.item.contents[j].compat.capacity)
                                                }
                                            }
                                            if(totalRam <= parseInt(item.specs.Max_Memory_Size)){
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: true, message: ''})
                                            }
                                            else {
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: false, message: 'total ram  ' + totalRam + 'GB is more than ' + item.specs.Max_Memory_Size + ' maximum ram size on the motherboard'})
                                            }
                                        }
                                        break
                                }
                                break
                            case 'graphics':
                                switch(option){
                                    case 'pci_e_x16':
                                        if (buildItem){
                                            var selectedGraphics = slotContents(buildItem)
                                            var portsAvail = compatCount(item, 'pci_e_x16')
                                            var portsOccupied = selectedGraphics.length
                                            if(portsOccupied <= portsAvail){
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: true, message: '', total: portsAvail})
                                            }
                                            else {
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: false, total: portsAvail, message: 'number of selected graphics cards ' + portsOccupied + ' is more than ' + portsAvail + ' PCI-E x16 slots on this motherboard'})
                                            }
                                        }
                                        break
                                }
                                break
                            case 'storage':
                                switch(option){
                                    case 'interface':
                                        if (buildItem){
                                            var selectedStorage = slotContents(buildItem)
                                            var storageCompatible = true
                                            for(var s = 0; s < selectedStorage.length; s++){
                                                if(!storageInterfaceCompatibleWithMotherboard(selectedStorage[s], item)){
                                                    storageCompatible = false
                                                }
                                            }
                                            if(storageCompatible){
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: true, message: ''})
                                            }
                                            else {
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: false, message: 'this motherboard does not support every selected storage interface'})
                                            }
                                        }
                                        break
                                    case 'sata_slots':
                                        if (buildItem){
                                            var sataDevices = countStorageBySlot(slotContents(buildItem), 'sata')
                                            var sataSlots = compatCount(item, 'sata_slots')
                                            if(sataDevices <= sataSlots){
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: true, message: ''})
                                            }
                                            else {
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: false, message: 'number of selected sata devices ' + sataDevices + ' is more than ' + sataSlots + ' SATA slots on this motherboard'})
                                            }
                                        }
                                        break
                                    case 'm.2_slots':
                                        if (buildItem){
                                            var m2Devices = countStorageBySlot(slotContents(buildItem), 'm2')
                                            var m2Slots = compatCount(item, 'M_2_Slot')
                                            if(m2Devices <= m2Slots){
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: true, message: ''})
                                            }
                                            else {
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: false, message: 'number of selected m.2 devices ' + m2Devices + ' is more than ' + m2Slots + ' M.2 slots on this motherboard'})
                                            }
                                        }
                                        break
                                }
                                break
                            case 'psu':
                                switch(option){
                                    case 'form_factor':
                                        // item = motherboard (build size), buildItem = PSU already in the build.
                                        var itemCompat      = item.compat.form_factor;
                                        var buildItemCompat = buildItem.item.contents.compat['form_factor'];
                                        var psuFit          = psuFormFactorFits(buildItemCompat, itemCompat);
                                        if(psuFit === false){
                                            commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: 'form_factor', value: false, message: buildItemCompat +' psu does not fit into your ' + itemCompat + ' build'})
                                        }
                                        else {
                                            commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: 'form_factor', value: true, message: ''})
                                        }
                                }
                                break
                            case 'case':
                                switch(option){
                                    case 'form_factor':
                                        // item = motherboard being added, buildItem = case in build (may list
                                        // multiple accepted board sizes). board = itemCompat, case = buildItemCompat.
                                        var itemCompat      = item.compat.form_factor;
                                        var buildItemCompat = buildItem.item.contents.compat['form_factor'];
                                        var caseFit         = caseBoardFormFactorFit(itemCompat, buildItemCompat);
                                        if(caseFit.kind === 'compatible'){
                                            commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: 'form_factor', value: true, message: ''})
                                        }
                                        else if(caseFit.kind === 'warning'){
                                            commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: 'form_factor', value: true, warning: true, message: 'Compatible fit: the '+ itemCompat +' motherboard fits the ' + buildItemCompat + ' case; some interior space will be unused'})
                                        }
                                        else if(caseFit.reason === 'small'){
                                            commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: 'form_factor', value: false, message: 'Your ' + buildItemCompat + ' case is too small for this motherboard ' + itemCompat + ' size'})
                                        }
                                        else {
                                            commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: 'form_factor', value: false, message: itemCompat +' case does not fit into your ' + buildItemCompat + ' build'})
                                        }
                                }
                                break
                        }
                        break
                    // RAM              Compatibility Check
                    case 'ram':
                        switch (withType){                          // <<<<<<<<<< WITH THIS
                            case 'cpu':
                                switch (option){
                                    case 'ram_type':
                                        if (buildItem){
                                            if(memoryTypesCompatible(buildItem.item.contents.compat.ram, item.compat.ram_type)){
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: true, message: ''})
                                            }
                                            else {
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: false, message: 'cpu ram ' + buildItem.item.contents.compat.ram + ' is incompatible with ' + item.compat.ram_type + ' ram'})
                                            }
                                        }
                                        break
                                    case 'ram_speed':
                                            if (buildItem){
                                                if(parseInt(buildItem.item.contents[0] ? buildItem.item.contents[0].compat.ram_speed : buildItem.item.contents.compat.ram_speed) == parseInt(item.compat ? item.compat.ram_speed : item[0].compat.ram_speed)){
                                                    commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: true, message: ''})
                                                }
                                                else if(parseInt(buildItem.item.contents[0] ? buildItem.item.contents[0].compat.ram_speed : buildItem.item.contents.compat.ram_speed) < parseInt(item.compat ? item.compat.ram_speed : item[0].compat.ram_speed) || parseInt(buildItem.item.contents[0] ? buildItem.item.contents[0].compat.ram_speed : buildItem.item.contents.compat.ram_speed) > parseInt(item.compat ? item.compat.ram_speed : item[0].compat.ram_speed)){
                                                    if(state.menuOptions.motherboard.item.contents){
                                                        if((parseInt(state.menuOptions.motherboard.item.contents.compat.ram_speed) < parseInt(buildItem.item.contents[0] ? buildItem.item.contents[0].compat.ram_speed : buildItem.item.contents.compat.ram_speed))
                                                        && (parseInt(state.menuOptions.motherboard.item.contents.compat.ram_speed) < parseInt(item.compat ? item.compat.ram_speed : item[0].compat.ram_speed))
                                                        ){
                                                            commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: true, warning: true, message: `RAM SPEED MISMATCH \n your computer will run at ${parseInt(state.menuOptions.motherboard.item.contents.compat.ram_speed)} Mhz`})
                                                        }
                                                    }
                                                    else{
                                                        commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: true, warning: true, message: `RAM SPEED MISMATCH \n your computer will run at ${parseInt(item.compat.ram_speed) < parseInt(buildItem.item.contents.compat.ram_speed) ? parseInt(item.compat.ram_speed) : parseInt(buildItem.item.contents.compat.ram_speed)} Mhz`})
                                                    }
                                                }
                                                // else {
                                                //     commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: false, message: 'cpu ram ' + buildItem.item.contents.compat.ram_type + ' is incompatible with' + item ? item.compat.ram_speed : item[0].compat.ram_speed + 'on the motherboard'})
                                                // }
                                            }
                                            break
                                    case 'max_memory':
                                            if (state.menuOptions.ram.item.contents && buildItem){
                                                var totalRam = 0
                                                if(state.menuOptions.ram.item.contents && state.menuOptions.ram.item.contents.length >0){
                                                    for(var j = 0; j < state.menuOptions.ram.item.contents.length; j++){
                                                        totalRam += parseInt(state.menuOptions.ram.item.contents[j].compat.capacity)
                                                    }
                                                }
                                                if(totalRam <= parseInt(buildItem.item.contents.specs.Max_Memory_Size)){
                                                    commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: true, message: ''})
                                                }
                                                else if(totalRam > parseInt(buildItem.item.contents.specs.Max_Memory_Size)){
                                                    commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: false, message: 'total ram  ' + totalRam + 'GB is more than ' + buildItem.item.contents.specs.Max_Memory_Size + ' maximum ram size on the cpu'})
                                                }
                                            }
                                        break
                                }
                                break
                            case 'motherboard':
                                switch (option){
                                    case 'ram_type':
                                        if (buildItem){
                                            if(memoryTypesCompatible(item.compat.ram_type, buildItem.item.contents.compat.ram)){
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: true, message: ''})
                                            }
                                            else {
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: false, message: 'ram ' + buildItem.item.contents.compat.ram_type + ' is incompatible with ' + item.compat.ram + ' motherboard ram'})
                                            }
                                        }
                                    break
                                    case 'max_memory':
                                        if (state.menuOptions.ram.item.contents && buildItem){
                                            var totalRam = 0
                                            if(state.menuOptions.ram.item.contents && state.menuOptions.ram.item.contents.length >0){
                                                for(var j = 0; j < state.menuOptions.ram.item.contents.length; j++){
                                                    totalRam += parseInt(state.menuOptions.ram.item.contents[j].compat.capacity)
                                                }
                                            }
                                            if(totalRam <= parseInt(buildItem.item.contents.specs.Max_Memory_Size)){
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: true, message: ''})
                                            }
                                            else {
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: false, message: 'total ram  ' + totalRam + 'GB is more than ' + 'maximum ram size on the motherboard'})
                                            }
                                        }
                                        break
                                    case 'ram_speed':
                                        if (buildItem){
                                            if(parseInt(buildItem.item.contents.compat.ram_speed) == parseInt(item.compat.ram_speed)){
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: true, message: ''})
                                            }
                                            else if(parseInt(buildItem.item.contents.compat.ram_speed) < parseInt(item.compat.ram_speed) || parseInt(buildItem.item.contents.compat.ram_speed) > parseInt(item.compat.ram_speed)){
                                                if(state.menuOptions.cpu.item.contents){
                                                    if((parseInt(state.menuOptions.cpu.item.contents.compat.ram_speed) < parseInt(buildItem.item.contents.compat.ram_speed))
                                                    && (parseInt(state.menuOptions.cpu.item.contents.compat.ram_speed) < parseInt(item.compat.ram_speed))
                                                    ){
                                                        commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: true, warning: true, message: `RAM SPEED MISMATCH \n your computer will run at ${parseInt(state.menuOptions.cpu.item.contents.compat.ram_speed)} Mhz`})
                                                    }
                                                }
                                                else{
                                                    commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: true, warning: true, message: `RAM SPEED MISMATCH \n your computer will run at ${parseInt(item.compat.ram_speed) < parseInt(buildItem.item.contents.compat.ram_speed) ? parseInt(item.compat.ram_speed) : parseInt(buildItem.item.contents.compat.ram_speed)} Mhz`})
                                                }
                                            }
                                            // else {
                                            //     commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: false, message: 'motherboard ram speed ' + buildItem.item.contents.compat.ram_speed + ' is incompatible with' + item.compat.ram_speed + ' speed ram'})
                                            // }
                                        }
                                        break
                                    case 'ram_slots':
                                        if (state.menuOptions.ram.item.contents && buildItem){
                                            var totalSticks = 0
                                            if(state.menuOptions.ram.item.contents && state.menuOptions.ram.item.contents.length >0){
                                                for(var j = 0; j < state.menuOptions.ram.item.contents.length; j++){
                                                    totalSticks += parseInt(state.menuOptions.ram.item.contents[j].compat.number_of_items)
                                                }
                                            }
                                            if(totalSticks <= parseInt(buildItem.item.contents.compat.ram_slots)){
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: true, message: ''})
                                            }
                                            else {
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: false, message: 'number of ram sticks ' + totalSticks + ' is more than ' + buildItem.item.contents.compat.ram_slots + ' slots on the motherboard'})
                                            }
                                        }
                                        break
                                }
                                break
                        }    
                        break
                    // Storage          Compatibility Check
                    case 'storage':
                        switch (withType){                          // <<<<<<<<<< WITH THIS
                            case 'motherboard':
                                switch (option){
                                    case 'interface':
                                        if (buildItem){
                                            if(storageInterfaceCompatibleWithMotherboard(item, buildItem.item.contents)){
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: true, message: ''})
                                            }
                                            else {
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: false, message: 'motherboard does not have compatible storage interface'})
                                            }
                                        }
                                        break
                                    case 'sata':
                                        if (buildItem && isSataStorage(item)){
                                            if(compatCount(buildItem.item.contents, 'sata_slots') > 0){
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: true, message: ''})
                                            }
                                            else {
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: false, message: 'motherboard does not have compatible interface'})
                                            }
                                        }
                                        break
                                    case 'sata_slots':
                                        if (state.menuOptions.storage.item.contents && buildItem){
                                            var totalSlots = countStorageBySlot(slotContents(state.menuOptions.storage), 'sata')
                                            if(totalSlots <= compatCount(buildItem.item.contents, 'sata_slots')){
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: true, message: ''})
                                            }
                                            else {
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: false, message: 'number of sata devices ' + totalSlots + ' is more than ' + buildItem.item.contents.compat.sata_slots + ' on the motherboard'})
                                            }
                                        }
                                        break
                                    case 'm.2_slots':
                                        if (state.menuOptions.storage.item.contents && buildItem){
                                            var totalSlots = countStorageBySlot(slotContents(state.menuOptions.storage), 'm2')
                                            if(totalSlots <= compatCount(buildItem.item.contents, 'M_2_Slot')){
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: true, message: ''})
                                            }
                                            else {
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: false, message: 'number of m.2 devices ' + totalSlots + ' is more than ' + buildItem.item.contents.compat.M_2_Slot + ' slots on the motherboard'})
                                            }
                                        }
                                        break
                                }
                                break
                        }    
                        break
                    // Graphics         Compatibility Check
                    case 'graphics':
                    case 'gpu':
                        switch (withType){                          // <<<<<<<<<< WITH THIS
                            case 'motherboard':
                                switch(option){
                                    case 'pci_e_x16':
                                        if (state.menuOptions.motherboard.item.contents && buildItem){
                                            var portsAvail = parseInt(buildItem.item.contents.compat.pci_e_x16)
                                            var portsOccupied = 0
                                            if(state.menuOptions.graphics.item.contents && state.menuOptions.graphics.item.contents.length >0){
                                                for(var j = 0; j < state.menuOptions.graphics.item.contents.length; j++){
                                                    portsOccupied++
                                                }
                                            }
                                            if(state.menuOptions.graphics.item.contents && portsOccupied <= portsAvail && portsOccupied !==0){
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: true, message: '', total: portsAvail})
                                            }
                                            else if (state.menuOptions.graphics.item.contents && portsOccupied > portsAvail){
                                                commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: option, value: false, total: portsAvail, message: 'number of PCI-E devices ' + portsOccupied + ' is more than ' +portsAvail + ' slots on the motherboard'})
                                            }
                                        }
                                        break
                                }
                                break
                        }    
                        break
                    // Power Supply Compatibility Check
                    case 'psu':
                        switch (withType){                          // <<<<<<<<<< WITH THIS
                            case 'general':
                                var itemWattage = parseFloat(item.specs.wattage)
                                function buildWattage(){ return state.buildTDP}
                                if(buildWattage() >0 && (itemWattage*0.8 > buildWattage())){
                                    commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: 'power', value: true, message: ''})
                                }
                                else if ((buildWattage() >0) && ((itemWattage*0.8) <= buildWattage())){
                                    commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: 'power', value: false, message: itemWattage+' psu does not have enough wattage to power your build' + buildWattage()})
                                }
                                break
                            case 'motherboard':
                                switch(option){
                                    case 'form_factor':
                                        // item = PSU being added, buildItem = motherboard (build size).
                                        var itemCompat      = item.compat.form_factor;
                                        var buildItemCompat = buildItem.item.contents.compat['form_factor'];
                                        var psuFit          = psuFormFactorFits(itemCompat, buildItemCompat);
                                        if(psuFit === false){
                                            commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: 'form_factor', value: false, message: itemCompat +' psu does not fit into your ' + buildItemCompat + ' build'})
                                        }
                                        else {
                                            commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: 'form_factor', value: true, message: ''})
                                        }
                                }
                                break
                            case 'case':
                                switch(option){
                                    case 'form_factor':
                                        // item = PSU being added, buildItem = case already in the build.
                                        var itemCompat      = item.compat.form_factor;
                                        var buildItemCompat = buildItem.item.contents.compat['form_factor'];
                                        var psuFit          = psuFormFactorFits(itemCompat, buildItemCompat);
                                        if(psuFit === false){
                                            commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: 'form_factor', value: false, message: itemCompat +' psu does not fit your ' + buildItemCompat + ' case'})
                                        }
                                        else {
                                            commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: 'form_factor', value: true, message: ''})
                                        }
                                }
                                break
                        }
                        break
                    // Case Compatibility Check
                    case 'case':
                        switch (withType){                          // <<<<<<<<<< WITH THIS
                            case 'cpu':
                                break
                            case 'motherboard':
                                switch(option){
                                    case 'form_factor':
                                        // item = case being added (may list multiple accepted board sizes),
                                        // buildItem = motherboard in build. board = buildItemCompat, case = itemCompat.
                                        var itemCompat      = item.compat.form_factor;
                                        var buildItemCompat = buildItem.item.contents.compat['form_factor'];
                                        var caseFit         = caseBoardFormFactorFit(buildItemCompat, itemCompat);
                                        if(caseFit.kind === 'compatible'){
                                            commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: 'form_factor', value: true, message: ''})
                                        }
                                        else if(caseFit.kind === 'warning'){
                                            commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: 'form_factor', value: true, warning: true, message: 'Compatible fit: the '+ buildItemCompat +' motherboard fits the ' + itemCompat + ' case; some interior space will be unused'})
                                        }
                                        else if(caseFit.reason === 'small'){
                                            commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: 'form_factor', value: false, message: 'Your ' + itemCompat + ' case is too small for this motherboard ' + buildItemCompat + ' size'})
                                        }
                                        else {
                                            commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: 'form_factor', value: false, message: itemCompat +' case does not fit into your ' + buildItemCompat + ' build'})
                                        }
                                }
                                break
                            case 'psu':
                                switch(option){
                                    case 'form_factor':
                                        // item = case being added, buildItem = PSU already in the build.
                                        var itemCompat      = item.compat.form_factor;
                                        var buildItemCompat = buildItem.item.contents.compat['form_factor'];
                                        var psuFit          = psuFormFactorFits(buildItemCompat, itemCompat);
                                        if(psuFit === false){
                                            commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: 'form_factor', value: false, message: buildItemCompat +' psu does not fit your ' + itemCompat + ' case'})
                                        }
                                        else {
                                            commit(recordCompat, {type: item.type, asin: item.asin, withType: withType, option: 'form_factor', value: true, message: ''})
                                        }
                                }
                                break
                        }
                        break
                }
            }
        }
    },
    async addToBuild        ({ commit }, item){
        if(!isPartAvailable(item)){
            return false
        }
        commit('newBuildItem', item);
        console.log('item', item.type, ' added to build')
        return true
    },
    async addToMenu         ({ commit }, item){
        if(!isPartAvailable(item)){
            return false
        }
        commit('newMenuItem', item);
        // Recalculate power before rebuilding per-item compatibility. The UI
        // calls getPrice next, but waiting for that left the compatibility
        // library one build change behind.
        commit('count')
        store.dispatch('compatTableReset')
        return true
    },
    async replaceBuildSlot  ({ commit, dispatch, state }, item){
        if(!item || !isPartAvailable(item)){
            return null
        }
        var definition = getPartDefinition(item.type)
        if(!definition){ return null }
        var previous = buildSlotSnapshot(state, definition.key)
        commit('REPLACE_BUILD_SLOT', { definition: definition, item: item })
        commit('count')
        await dispatch('compatTableReset')
        return previous
    },
    async restoreBuildSlot  ({ commit, dispatch }, snapshot){
        if(!snapshot || !getPartDefinition(snapshot.type)){
            return false
        }
        commit('RESTORE_BUILD_SLOT', snapshot)
        commit('count')
        await dispatch('compatTableReset')
        return true
    },
    async startBuildWithPart  ({ commit, dispatch, state }, item){
        if(!item || !isPartAvailable(item)){
            return null
        }
        var definition = getPartDefinition(item.type)
        if(!definition){ return null }
        var previous = buildStateSnapshot(state)
        commit('RESET_BUILD')
        commit('REPLACE_BUILD_SLOT', { definition: definition, item: item })
        commit('count')
        await dispatch('compatTableReset')
        return previous
    },
    async restoreBuildSnapshot  ({ commit, dispatch }, snapshot){
        if(!snapshot || !snapshot.menuOptions || !snapshot.build || !snapshot.buildRaw){
            return false
        }
        commit('RESTORE_BUILD_SNAPSHOT', snapshot)
        commit('count')
        await dispatch('compatTableReset')
        return true
    },
    async getPrice          ({ commit }){
        console.log('get price initiated')
        commit('count')
        console.log('total cost commited')
    },
    async rmFromBuild       ({ commit }, {item, id}){
        commit('rmBuildItem', {item, id}); 
    },
    async rmFromMenu        ({ commit }, item){
        commit('rmMenuItem', item);
        commit('count')
        store.dispatch('compatTableReset')
        console.log(item + ' removed from menu') 
    },
    // Write changes back to the saved build the builder currently has open.
    // Mirrors saveBuild's payload with the build id added; the backend route is
    // POST /api/builds/edit -> BuildController.updateBuild.
    async updateSavedBuild  ({commit, state}, {...data}){
        var buildId = data.id !== undefined ? data.id : state.savedBuildId
        if(buildId === null || buildId === undefined || buildId === ''){
            store.dispatch('setError', 'This build has not been saved yet.')
            return null
        }
        return await axios({
            url: `${SERVER_PATH}builds/edit`,
            method: "post",
            headers: {
                'Content-Type': `application/json`,
                'Authorization': `Bearer ${localStorage.usertoken}`,
                'alg': "HS256",
                'typ': "JWT"
            },
            data: Object.assign({}, data, { id: buildId })
        })
        .then((response) => {
            var payload = JSON.stringify(response.data)
            if(payload.includes('ERROR') || payload.includes('error')){
                store.dispatch('setError', (response.data && response.data.sqlMessage) || 'Could not update this build.')
                return response
            }
            commit('MARK_BUILD_SAVED', { id: buildId, name: data.name })
            store.dispatch('setBuildAdded', ' was updated')
            return response
        })
        .catch((err) => {
            console.log('build update failed', err)
            store.dispatch('setError', saveErrorMessage(err))
            return null
        })
    },
    // POST /api/builds/add answers with { Messege, StatusCode, data } and NO id
    // for the row it just inserted. Without one the builder stayed detached, so
    // pressing Save a second time created a duplicate instead of updating.
    // Re-read the library and adopt the newest build carrying this name.
    async adoptCreatedBuild ({commit}, {name, authorId}){
        if(!authorId || !name){ return null }
        try {
            await store.dispatch('fetchUserBuilds', authorId)
        }
        catch(e){
            return null
        }
        var builds = store.getters.getUserBuilds || []
        var wanted = String(name).trim().toLowerCase()
        var match = builds
            .filter((build) => build && String(build.name || '').trim().toLowerCase() === wanted)
            // Highest id wins: same-named builds are ordered by creation, and the
            // one we just inserted is the newest.
            .sort((left, right) => Number(right.id || 0) - Number(left.id || 0))[0]
        if(!match){ return null }
        commit('MARK_BUILD_SAVED', { id: match.id, name: match.name })
        return match.id
    },
    async saveBuild         ({commit, dispatch}, {...data}){
        console.log(data)
        console.log('sending this: ', localStorage.usertoken)
        return await axios({
            url: `${SERVER_PATH}builds/add`,
            method: "post",
            headers: {
                'Content-Type': `application/json`,
                'Authorization': `Bearer ${localStorage.usertoken}`,
                'alg': "HS256",
                'typ': "JWT"
            },
            data: data
        })
        .then(async (response) => {
            console.log(JSON.stringify(response.data))
            if (JSON.stringify(response.data).includes('User id is empty')){
                store.dispatch('setError', 'User Id is Empty')
            }
            else if (JSON.stringify(response.data).includes('Added')){
                var message = ' was added to '
                console.log(message)
                store.dispatch('setBuildAdded', message)
                // Re-baseline first so the build reads clean even if the lookup
                // below cannot reach the API; then adopt the id so the NEXT save
                // updates this build instead of inserting another copy.
                commit('MARK_BUILD_SAVED', { id: null, name: data.name })
                await dispatch('adoptCreatedBuild', { name: data.name, authorId: data.author_id })
            }
            else if (JSON.stringify(response.data).includes('ERROR' || 'error')){
                if (JSON.stringify(response.data).includes('sqlMessage')) {
                    store.dispatch('setError', response.data.sqlMessage)
                }
                else {
                    store.dispatch('setError', 'Unknown Error')
                }
            }
            console.log(response)
            return response
        })
        .catch((err) => {
            console.log('error!!!', err)
            store.dispatch('setError', saveErrorMessage(err))
        })
    },
    async rmBuild           ({ commit }, build_id){
        commit('PROCESSING', true)
        console.log('removing build with id ', build_id)
        return await axios({
            url: `${SERVER_PATH}builds?id=${build_id}`,
            method: "delete",
            headers: {
                'Content-Type': `application/json`,
                'Authorization': `Bearer ${localStorage.usertoken}`,
                'alg': "HS256",
                'typ': "JWT"
            },
        }).then(async (response)=>{
            store.dispatch('fetchUserBuilds', JSON.parse(localStorage.user).id)
            console.log(response)
            commit('PROCESSING', false)
        }).catch((err) => {
            commit('PROCESSING', false)
            console.log(err)
        })
    },
    // Open a saved build for editing: the builder remembers which build it is,
    // so Save can update it in place instead of silently creating a duplicate.
    loadBuild               ({dispatch}, build){
        return dispatch('applyBuild', { build: build, asCopy: false })
    },
    // Start a new build seeded from an existing one (the "Clone" action). Shares
    // all the loading logic but deliberately takes no identity, so saving can
    // never overwrite the build it was copied from.
    cloneBuild              ({dispatch}, build){
        return dispatch('applyBuild', { build: build, asCopy: true })
    },
    applyBuild              ({commit}, {build, asCopy}){
        console.log('loading build ', build)
        commit('RESET_BUILD')
        var fieldExceptions = [
            'name',
            'accessories',
            'image',
            'id',
            'author_id',
            'cost',
            'status'
          ]
        for (var prop in build){
            if(!fieldExceptions.includes(prop) && !prop.includes('_')){
                if(build[prop] !== null){
                    if(typeof(build[prop]) == 'object' && build[prop].length >=1){
                        for(var i = 0; i<build[prop].length; i++){
                            commit('newBuildItem', build[prop][i])
                            commit('newMenuItem', build[prop][i])
                        }
                    }
                    else {
                        commit('newBuildItem', build[prop])
                        commit('newMenuItem', build[prop])
                    }
               
                }
                console.log(prop, build[prop])
            }
        }
        console.log('loading build: ', build)
        store.dispatch('compatTableReset')
        var startIndex = null
        if(build['processor'] && build['processor'].id){
            startIndex = build['processor'].id
        }
        if(startIndex){
            router.push({path: "/builder/cpu", query: { view: "slider", id: startIndex }}).catch(()=>{})
        }
        else {
            router.push({path: "/builder/cpu"}).catch(()=>{})
        }

        commit('count')

        // Attach after the parts are in place so the baseline fingerprint is
        // taken from the loaded build, not from the empty state RESET left.
        if(!asCopy && build && build.id !== undefined && build.id !== null){
            commit('ATTACH_SAVED_BUILD', { id: build.id, name: build.name || '' })
        }
    },
    buildReset              ({commit}){
        commit('RESET_BUILD')
        // Reset means "start a new build" -- it must not keep pointing at the
        // saved build it was cleared from, or the next save would blank that out.
        commit('DETACH_SAVED_BUILD')
        store.dispatch('compatTableReset')
        commit('count')
    },
    detachSavedBuild        ({commit}, options){
        commit('DETACH_SAVED_BUILD', options)
    },
    setDraftBuildName       ({commit}, name){
        commit('SET_DRAFT_BUILD_NAME', { name: name, automatic: false })
    },
    setAutomaticBuildName   ({commit}, name){
        commit('SET_DRAFT_BUILD_NAME', { name: name, automatic: true })
    },
    compatTableReset    ({commit, state}){
        commit('RESET_COMPAT_TABLE')
        commit('RESET_COMPAT_LIB')
        store.commit('assigningItems', store.getters.getFetchedItems)
        functions.createCompatTable({build: state.menuOptions})
    },
    resetWarnings({commit}){
        commit('RESET_WARNINGS')
    },
    compatLibReset    ({commit}){
        commit('RESET_COMPAT_LIB')

    }
};

const mutations = {
    // Bind the builder to a saved build and take the baseline fingerprint.
    ATTACH_SAVED_BUILD(state, {id, name}){
        state.savedBuildId = id === undefined ? null : id
        state.savedBuildName = name || ''
        state.draftBuildName = name || ''
        state.draftBuildNameAutomatic = false
        state.savedSignature = buildSignature(state.buildRaw)
        persistBuildState(state)
    },
    // Back to an unsaved build (Reset, or after cloning someone else's build).
    // keepDraft is for "Save as new": the title the user just typed has to
    // survive detaching from the build it was copied from.
    DETACH_SAVED_BUILD(state, options){
        state.savedBuildId = null
        state.savedBuildName = ''
        state.savedSignature = ''
        if(!options || !options.keepDraft){
            state.draftBuildName = ''
            state.draftBuildNameAutomatic = true
        }
        persistBuildState(state)
    },
    // Re-baseline after a successful write, so the build reads as clean again.
    MARK_BUILD_SAVED(state, {id, name}){
        if(id !== undefined && id !== null && id !== ''){
            state.savedBuildId = id
        }
        if(name){
            state.savedBuildName = name
            state.draftBuildName = name
        }
        state.draftBuildNameAutomatic = false
        state.savedSignature = buildSignature(state.buildRaw)
        persistBuildState(state)
    },
    SET_DRAFT_BUILD_NAME(state, payload){
        var value = payload && typeof payload == 'object' ? payload.name : payload
        state.draftBuildName = value === undefined || value === null ? '' : String(value)
        state.draftBuildNameAutomatic = !!(payload && typeof payload == 'object' && payload.automatic)
        persistBuildState(state)
    },
    REPLACE_BUILD_SLOT(state, {definition, item}){
        var multiple = definition.menuKey == 'ram'
            || definition.menuKey == 'storage'
            || definition.menuKey == 'graphics'
        var contents = multiple ? [cloneBuildValue(item)] : cloneBuildValue(item)
        var rawValue = multiple
            ? JSON.stringify([item.id])
            : item.id
        writeBuildSlot(state, {
            type: definition.key,
            buildContents: contents,
            menuContents: contents,
            rawValue: rawValue,
        })
    },
    RESTORE_BUILD_SLOT(state, snapshot){
        writeBuildSlot(state, snapshot)
    },
    RESTORE_BUILD_SNAPSHOT(state, snapshot){
        state.menuOptions = cloneBuildValue(snapshot.menuOptions)
        state.build = cloneBuildValue(snapshot.build)
        state.buildRaw = cloneBuildValue(snapshot.buildRaw)
        persistBuildState(state)
    },
    RESET_WARNINGS(state){
        state.warnings = []
    },
    UPDATE_BUILD_STATUS(state, status){
        state.buildStatus = status
    },
    SET_COMPAT_MODE(state, bool){
        state.compatibleMode = bool
    },
    RESET_COMPAT_TABLE  (state){
        state.stateCompatTable = {}
    },
    RESET_COMPAT_LIB  (state){
        state.compatLib = {}
    },
    BUILDS_LOADED       (state, bool){
        state.buildsLoaded = bool
    },
    RECORD_FETCHED_BUILDS(state, builds){     
        state.recentBuilds = [...builds]
    },
    RESET_BUILD         (state){
        console.log('build reset started, got menu: ', Object.keys(state.menuOptions), ' build: ', state.build)
        var keysArr = Object.keys(state.menuOptions)
        for(var i = 0; i< keysArr.length; i++){
            state.menuOptions[keysArr[i]].item.contents = null
        }
        for(var j = 0; j< state.build.length; j++){
            state.build[j].contents = null
        }
        var rawKeys = Object.keys(state.buildRaw)
        for(var k = 0; k< rawKeys.length; k++){
            if(rawKeys[k] == 'ram_id' || rawKeys[k] == 'storage_id' || rawKeys[k] == 'graphics_id'){
                state.buildRaw[rawKeys[k]] = []
            }
            else {
                state.buildRaw[rawKeys[k]] = ''
            }
        }
        persistBuildState(state)
    },
    RECORD_STATE_COMPAT       (state, {type, withType, option, value, message, warning}){
        if(!state.stateCompatTable[type]){
            state.stateCompatTable[type] = {}
        }
        if(!state.stateCompatTable[type][withType]){
            state.stateCompatTable[type][withType] = {}
        }
        state.stateCompatTable[type][withType][option] = {
            compatible: value,
            message: message,
            warning: warning || ''
        }
        if(!state.warningsTable[type]){
            state.warningsTable[type] = {}
        }
        if(!state.warningsTable[type][withType]){
            state.warningsTable[type][withType] = {}
        }
        state.warningsTable[type][withType][option] = {
            message: message,
        }
        if(state.warnings && warning){
            if(state.warnings.length > 0 && !state.warnings.includes(message)){
                var tmp = [...state.warnings]
               
                tmp.push(message)
                state.warnings = [...tmp]
            }
            else if(!state.warnings.includes(message)) {var tmp = []; tmp.push(message); state.warnings = [...tmp]}
        }
    },
    RECORD_COMPAT       (state, {type, withType, option, value, message, warning}){
        if(!state.compatTable[type]){
            state.compatTable[type] = {}
        }
        if(!state.compatTable[type][withType]){
            state.compatTable[type][withType] = {}
        }
        state.compatTable[type][withType][option] = {
            compatible: value,
            message: message,
            warning: warning || ''
        }
    },
    RECORD_LIB_COMPAT   (state, {type, asin, withType, option, value, message, warning}){
        if(!state.compatLib[asin]){
            state.compatLib[asin] = {}
        }
        if(!state.compatLib[asin][type]){
            state.compatLib[asin][type] = {}
        }
        if(!state.compatLib[asin][type][withType]){
            state.compatLib[asin][type][withType] = {}
        }
        state.compatLib[asin][type][withType][option] = {
            compatible: value,
            message: message,
            warning: warning || ''
        }
    },
    PROCESSING          (state, bool){
        state.processing = bool
    },
    count               (state) {
       const localBuild = state.build
        //    console.log('counting build total price: ', localBuild)
       state.totalCost = 0
       var tdp = 0
        for (var i = 0; i < localBuild.length; i++){
            // console.log('looping ', i, ' through the build, ', localBuild[i])
            if (localBuild[i].contents !== null){
                // console.log('checking type ', localBuild[i].type)
                if(localBuild[i].type == 'ram'||localBuild[i].type == 'storage'||localBuild[i].type == 'gpu'|| localBuild[i].type == 'graphics'){
                    // console.log('caught array type ', localBuild[i].contents)
                    if(typeof(localBuild[i].contents) == 'object' && localBuild[i].contents.length >0){
                        // console.log('type ', localBuild[i].type, ' is not empty')
                        for(var j = 0; j< localBuild[i].contents.length; j++){
                            // console.log('counting array ', localBuild[i].contents[j])
                            var arrayItemPrice = functions.parsePrice(localBuild[i].contents[j].amzn_price)
                            if(arrayItemPrice !== null){
                                state.totalCost += arrayItemPrice
                            }
                            if(localBuild[i].type == 'ram'){
                                tdp += 3*(parseInt(localBuild[i].contents[j].compat.capacity) / 8)
                            }
                            else{
                                tdp += parseInt(localBuild[i].contents[j].compat.tdp)
                            }
                        }
                    }
                }
                else {
                    // console.log('item with type ', state.build[i].contents.type, state.build[i].contents)
                    var singleItemPrice = functions.parsePrice(localBuild[i].contents.amzn_price)
                    if(singleItemPrice !== null){
                        state.totalCost += singleItemPrice
                    }
                    if(localBuild[i].type !== 'psu' && localBuild[i].type !== 'case'){
                        tdp += parseInt(localBuild[i].contents.compat.tdp)
                    }
                }
            }      
        }
        state.buildTDP = tdp
        EventBus.$emit("totalCostChanged", state.totalCost)
        persistBuildState(state)
    },
    newBuildItem:       (state, item) => {
        // console.log('build received ',  item)
        var itemLocal = item
        var localtype = itemLocal.type

        for (var i =0; i<state.build.length; i++){
            // console.log('starting build loop', item.type, state.build[i].type)
            if (localtype == 'graphics'){
                localtype = 'gpu'
            }
            if (localtype == state.build[i].type){
                if(localtype == 'cpu'){
                    localtype = 'processor'
                }
                console.log('adding ', item.type, 'to build type: ',  state.build[i].type)
                if(localtype == 'ram' || localtype == 'storage' || localtype == 'gpu' || localtype == 'graphics'){
                    // console.log('type ', localtype, ' is associated with array...')
                    var itemsArr = []
                    var idsArr = []
                    console.log('contents: ', state.build[i].contents)
                    if(state.build[i].contents !== null && typeof(state.build[i].contents) == 'object' && state.build[i].contents.length >0){
                        console.log('contents is not empty, saving...')
                        itemsArr = [...state.build[i].contents]
                    }
                    itemsArr.push(item)
                    console.log('ITEM PUSHED ', state.build[i].type)
                    state.build[i].contents = itemsArr;
                    console.log('item ', item, ' is added to build\'s', state.build[i].type, ' build: ', state.build)
                    for(var i = 0; i< itemsArr.length; i++){
                        idsArr.push(itemsArr[i].id)
                        console.log('getting items\' id ', idsArr)
                    }
                    state.buildRaw[`${
                        localtype == 'gpu' ? 'graphics'
                        : localtype
                        }_id`] = JSON.stringify(idsArr)
                    console.log('id was added to buildRaw ', state.buildRaw)
                    persistBuildState(state)
                    return
                }
                else {
                    state.build[i].contents = itemLocal;
                    state.buildRaw[`${localtype == "cpu" ? 'processor' : localtype}_id`] = state.build[i].contents.id
                    console.log('item added to build ', state.build[i].type, ' and item id is added to build id list', state.buildRaw)
                    persistBuildState(state)
                    return
                }
            }
        }  
    },
    rmBuildItem:        (state, {item, id}) => {
        // console.log('build received ',  item)
        var localtype = item.type
        if (localtype == 'graphics'){
            localtype = 'gpu'
        }
        for (var i =0; i<state.build.length; i++){
            // console.log('starting build loop', localtype, state.build[i].type)
            if (localtype == state.build[i].type){
                var rawKey = buildRawKeyForType(state.build[i].type)
                if(localtype == 'ram' || localtype == 'storage' || localtype == 'gpu'){
                    var ids = []
                    if(typeof(state.build[i].contents) == 'object' && id !== undefined && state.build[i].contents.length > 0){
                        var itemsArr = [...state.build[i].contents]
                        for(var l = 0; l < itemsArr.length; l++){
                            if(itemsArr[l].id == id){
                                console.log('found match, removing')
                                itemsArr.splice(l, 1)
                                break
                            }
                        }
                        state.build[i].contents = itemsArr
                        for(var k = 0; k <state.build[i].contents.length; k++){
                            ids.push(state.build[i].contents[k].id)
                        }
                        if(state.build[i].contents.length == 0){
                            state.build[i].contents = null
                            state.buildRaw[rawKey] = []
                        }
                        else {
                            state.buildRaw[rawKey] = JSON.stringify(ids)
                        }
                    }
                    else {
                        state.build[i].contents = null;
                        state.buildRaw[rawKey] = ids
                    }
                    console.log(item, ' item is removed from build') 
                }
                else {
                    state.buildRaw[rawKey] = ''
                    state.build[i].contents = null;
                    console.log(item, ' item is removed from build') 
                }
            }
        }  
        persistBuildState(state)
    },
    newMenuItem:        (state, item) => {
        console.log('adding to menu')
        if(item.type == 'cpu'){
            state.menuOptions.cpu.item.contents = item
            console.log('item ', item, ' with type ', item.type, ' is added to menu ', state.menuOptions)
        }
        if(item.type == 'motherboard'){
            state.menuOptions.motherboard.item.contents = item
            console.log('item ', item, ' with type ', item.type, ' is added to menu ', state.menuOptions)
        }
        var arr
        if(item.type == 'ram'){
            console.log('recognized type : ', item.type)
            if(state.menuOptions.ram.item.contents !== null && state.menuOptions.ram.item.contents.length >0){
                console.log('saving state: ', [...state.menuOptions.ram.item.contents])
                arr = [...state.menuOptions.ram.item.contents]
                arr.push(item)
                state.menuOptions.ram.item.contents = arr
                console.log('state updated: ', state.menuOptions.ram.item.contents)
            }
            else {
                state.menuOptions.ram.item.contents = [{...item}]
                console.log('ram contents added to menu: ',  [{...item}])
            }
            // state.menuOptions.ram.item.contents = item
        }
        if(item.type == 'storage'){
            console.log('recognized type : ', item.type)
            if(state.menuOptions.storage.item.contents !== null && state.menuOptions.storage.item.contents.length >0){
                console.log('saving state: ', [...state.menuOptions.storage.item.contents])
                arr = [...state.menuOptions.storage.item.contents]
                arr.push(item)
                state.menuOptions.storage.item.contents = arr
                console.log('state updated: ', state.menuOptions.storage.item.contents)
            }
            else {
                state.menuOptions.storage.item.contents = [{...item}]
                console.log('storage contents added to menu: ',  [{...item}])
            }
        }
        if(item.type == 'gpu' || item.type == 'graphics'){
            console.log('recognized type : ', item.type)
            if(state.menuOptions.graphics.item.contents !== null && state.menuOptions.graphics.item.contents.length >0){
                console.log('saving state: ', [...state.menuOptions.graphics.item.contents])
                arr = [...state.menuOptions.graphics.item.contents]
                arr.push(item)
                state.menuOptions.graphics.item.contents = arr
                console.log('state updated: ', state.menuOptions.graphics.item.contents)
            }
            else {
                state.menuOptions.graphics.item.contents = [{...item}]
                console.log('graphics contents added to menu: ',  [{...item}])
            }
        }
        if(item.type == 'psu'){
            state.menuOptions.psu.item.contents = item
            console.log('item ', item, ' with type ', item.type, ' is added to menu ', state.menuOptions)
        }
        if(item.type == 'case'){
            state.menuOptions.case.item.contents = item
            console.log('item ', item, ' with type ', item.type, ' is added to menu ', state.menuOptions)
        }
        if(item.type == 'accessories'){
            state.menuOptions.accessories.item.contents = item
        }
        persistBuildState(state)
    },
    rmMenuItem:         (state, {item, id}) => {
        if(item.type == 'cpu'){
            state.menuOptions.cpu.item.contents = null
        }
        if(item.type == 'motherboard'){
            state.menuOptions.motherboard.item.contents = null
        }
        var itemsArr
        if(item.type == 'ram'){
            if(state.menuOptions.ram.item.contents !== null && id !==undefined && typeof(state.menuOptions.ram.item.contents) == 'object' && state.menuOptions.ram.item.contents.length > 0){
                itemsArr = [...state.menuOptions.ram.item.contents]
                for(var l = 0; l < itemsArr.length; l++){
                    if(itemsArr[l].id == id){
                        console.log('found match, removing')
                        itemsArr.splice(l, 1)
                        break
                    }
                }
                state.menuOptions.ram.item.contents = itemsArr
                if(state.menuOptions.ram.item.contents.length == 0){
                    state.menuOptions.ram.item.contents = null
                }
            }
            else {
                state.menuOptions.ram.item.contents = null
            }
        }
        if(item.type == 'storage'){
            if(state.menuOptions.storage.item.contents !== null && id !==undefined && typeof(state.menuOptions.storage.item.contents) == 'object' && state.menuOptions.storage.item.contents.length > 0){
                itemsArr = [...state.menuOptions.storage.item.contents]
                for(var m = 0; m < itemsArr.length; m++){
                    if(itemsArr[m].id == id){
                        console.log('found match, removing')
                        itemsArr.splice(m, 1)
                        break
                    }
                }
                state.menuOptions.storage.item.contents = itemsArr
                if(state.menuOptions.storage.item.contents.length == 0){
                    state.menuOptions.storage.item.contents = null
                }
            }
            else {
                state.menuOptions.storage.item.contents = null
            }
        }
        if(item.type == 'gpu' || item.type == 'graphics'){
            if(state.menuOptions.graphics.item.contents !== null && id !==undefined && typeof(state.menuOptions.graphics.item.contents) == 'object' && state.menuOptions.graphics.item.contents.length > 0){
                itemsArr = [...state.menuOptions.graphics.item.contents]
                for(var n = 0; n < itemsArr.length; n++){
                    if(itemsArr[n].id == id){
                        console.log('found match, removing')
                        itemsArr.splice(n, 1)
                        break
                    }
                }
                state.menuOptions.graphics.item.contents = itemsArr
                if(state.menuOptions.graphics.item.contents.length == 0){
                    state.menuOptions.graphics.item.contents = null
                }
            }
            else {
                state.menuOptions.graphics.item.contents = null
            }
        }
        if(item.type == 'psu'){
            state.menuOptions.psu.item.contents = null
        }
        if(item.type == 'case'){
            state.menuOptions.case.item.contents = null
        }
        if(item.type == 'accessories'){
            state.menuOptions.accessories.item.contents = []
        }
        persistBuildState(state)
    },
};

export default {
    state,
    getters,
    actions,
    mutations
}
